import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon } from '@mui/icons-material';
import { Line, LineChart } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { MODEL_REGISTRY, isDeprecated } from '../../config/modelConfig';
import { PlatformRouteTests, Schedule, TestSettings, fmtCredits, fmtPctValue, fmtSeconds, testsApi, useTestsAccess } from '../../services/testsApi';
import { SectionTitle } from '../performance/common';
import { ApplyPicksDialog } from './ApplyPicksDialog';
import { PICK_LABELS, PickCell, Score, modelCountText, modelLabel, timeAgo } from './common';
import { errorText } from './dialogs';
import { ModelComparisonTable, rowsFromPublished } from './ModelComparison';
import { PlatformTestDialog } from './PlatformTestDialog';
import { ScheduleDialog, scheduleText } from './ScheduleDialog';
import { runUrl } from './TargetTests';

const AccuracySpark: React.FC<{ points: { default_accuracy: number | null }[] }> = ({ points }) =>
  points.length < 2 ? (
    <Typography variant="caption" color="text.secondary">
      –
    </Typography>
  ) : (
    <LineChart width={90} height={26} data={points}>
      <Line dataKey="default_accuracy" stroke="#2e7d32" dot={false} strokeWidth={1.5} connectNulls isAnimationActive={false} />
    </LineChart>
  );

/** A route whose latest test recommends a model it isn't already using. */
const canApply = (r: PlatformRouteTests) => {
  const rec = r.tested?.picks?.recommended?.model;
  return !!(r.tested_run_id && rec && rec !== r.current_default);
};

export const currentAccuracy = (r: PlatformRouteTests) =>
  r.tested?.models.find(m => m.model === r.current_default)?.accuracy ?? r.last_completed?.default_accuracy ?? null;

const csvCell = (v: unknown) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function exportCsv(routes: PlatformRouteTests[]) {
  const header = ['route', 'platform_tool', 'api_paths', 'cases', 'tested_at', 'current_default', 'current_accuracy',
    'recommended', 'recommended_accuracy', 'recommended_p95_s', 'recommended_credits', 'high_quality', 'high_quality_accuracy',
    'budget', 'budget_accuracy', 'budget_credits', 'recommendation', 'published'];
  const rows = routes.map(r => {
    const p = r.tested?.picks;
    return [r.name, r.platform_tool, r.api_paths.join(' '), r.cases, r.tested?.tested_at, r.current_default, currentAccuracy(r),
      p?.recommended?.model, p?.recommended?.accuracy, p?.recommended?.p95_s, p?.recommended?.credits_per_run,
      p?.quality?.model, p?.quality?.accuracy, p?.budget?.model, p?.budget?.accuracy, p?.budget?.credits_per_run,
      r.recommendation?.text, r.published];
  });
  const text = [header, ...rows].map(row => row.map(csvCell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `platform-route-accuracy-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Platform Insights "Accuracy & models": test results, picks and publishing for every platform route. */
export const AccuracyTab: React.FC<{ reload: number }> = ({ reload }) => {
  const { getIdToken } = useAuth();
  const { api, access } = useTestsAccess(getIdToken);
  const platformId = access?.platform_account_id || null;
  const platformApi = useMemo(() => (platformId ? testsApi(getIdToken, platformId) : null), [getIdToken, platformId]);
  const [routes, setRoutes] = useState<PlatformRouteTests[] | null>(null);
  const [schedule, setSchedule] = useState<Schedule | null | undefined>(undefined);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<React.ReactNode>(null);
  const [localReload, setLocalReload] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [applyTo, setApplyTo] = useState<PlatformRouteTests[] | null>(null);
  const [retestOpen, setRetestOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => setLocalReload(r => r + 1), []);

  useEffect(() => {
    let cancelled = false;
    api
      .platformResults()
      .then(d => !cancelled && setRoutes(d.routes))
      .catch(e => !cancelled && setError(errorText(e)));
    return () => {
      cancelled = true;
    };
  }, [api, reload, localReload]);

  useEffect(() => {
    if (!platformApi) return;
    let cancelled = false;
    platformApi
      .schedules()
      .then(d => !cancelled && setSchedule(d.schedules.find(s => s.scope === 'platform') || null))
      .catch(() => !cancelled && setSchedule(null));
    return () => {
      cancelled = true;
    };
  }, [platformApi, reload, localReload]);

  const togglePublish = async (r: PlatformRouteTests, published: boolean) => {
    try {
      await api.setPublished(r.family_id, published);
      setRoutes(rs => (rs ? rs.map(x => (x.family_id === r.family_id ? { ...x, published } : x)) : rs));
    } catch (e) {
      setError(errorText(e));
    }
  };

  const publishSelected = async (published: boolean) => {
    const targets = chosen.filter(r => r.published !== published && (r.tested || !published));
    setBusy(true);
    try {
      for (const r of targets) await api.setPublished(r.family_id, published);
      setNotice(`${published ? 'Published' : 'Unpublished'} ${targets.length} route${targets.length === 1 ? '' : 's'}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const undoApply = async (r: PlatformRouteTests) => {
    if (!window.confirm(`Put ${r.name} back on ${r.applied?.previous_models.map(m => modelLabel(m)).join(', ')}? This creates a new function version.`)) return;
    try {
      await api.undoApplyPicks({ family_id: r.family_id, customer_id: r.customer_id });
      setNotice(`Undid the applied picks on ${r.name}.`);
      refresh();
    } catch (e) {
      setError(errorText(e));
    }
  };

  if (error && !routes) return <Alert severity="error">{error}</Alert>;
  if (!routes) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;

  const switches = routes.filter(r => r.recommendation?.action === 'switch' && r.tested_run_id);
  const applicable = switches.filter(canApply);
  const chosen = routes.filter(r => selected.has(r.family_id));
  const chosenApplicable = chosen.filter(canApply);
  const allSelected = routes.length > 0 && chosen.length === routes.length;
  const flip = (s: Set<string>, id: string) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const toggle = (id: string) => setSelected(s => flip(s, id));
  const toggleExpanded = (id: string) => setExpanded(s => flip(s, id));
  const testedRoutes = routes.filter(r => r.tested?.models.length);
  const allExpanded = testedRoutes.length > 0 && testedRoutes.every(r => expanded.has(r.family_id));
  const retestModels = Array.from(new Set(chosen.flatMap(r => r.tested?.models.map(m => m.model) ?? [])));
  const q = (r: PlatformRouteTests) => r.customer_id;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <SectionTitle
          title="Recommended changes"
          help="Routes where a tested model beats the current default on the balance of accuracy, speed and cost. Review the run, then Apply to make Recommended the default model with Budget as its first backup."
          action={
            applicable.length > 1 && (
              <Button size="small" variant="outlined" onClick={() => setApplyTo(applicable)}>
                Apply all ({applicable.length})
              </Button>
            )
          }
        />
        {!switches.length ? (
          <Typography variant="body2" color="text.secondary">
            {routes.some(r => r.tested) ? 'Every tested route is on its recommended model.' : 'No routes tested yet. Test them from Platform Routes.'}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {switches.map(r => (
              <Box key={r.family_id} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {r.recommendation?.text}
                </Typography>
                <Link component={RouterLink} to={runUrl(r.tested_run_id as string, q(r))} variant="body2">
                  Review
                </Link>
                {canApply(r) && (
                  <Button size="small" onClick={() => setApplyTo([r])} sx={{ py: 0 }}>
                    Apply
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <SectionTitle
          title="Platform routes"
          help={`Latest completed test per route. ${PICK_LABELS.recommended.help} Published routes appear on the demo site and in the platform portal.`}
          action={
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              {chosen.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {chosen.length} selected
                  </Typography>
                  <Tooltip title={chosenApplicable.length ? '' : 'None of the selected routes have a different Recommended model'}>
                    <span>
                      <Button size="small" variant="contained" disabled={!chosenApplicable.length || busy} onClick={() => setApplyTo(chosenApplicable)}>
                        Apply picks ({chosenApplicable.length})
                      </Button>
                    </span>
                  </Tooltip>
                  <Button size="small" variant="outlined" disabled={busy} onClick={() => setRetestOpen(true)}>
                    Retest
                  </Button>
                  <Button size="small" variant="outlined" disabled={busy} onClick={() => publishSelected(true)}>
                    Publish
                  </Button>
                  <Button size="small" variant="outlined" disabled={busy} onClick={() => publishSelected(false)}>
                    Unpublish
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={!testedRoutes.length}
                onClick={() => setExpanded(allExpanded ? new Set() : new Set(testedRoutes.map(r => r.family_id)))}
              >
                {allExpanded ? 'Hide models' : 'Show all models'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => exportCsv(routes)} disabled={!routes.length}>
                Export CSV
              </Button>
            </Stack>
          }
        />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={chosen.length > 0 && !allSelected}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(routes.map(r => r.family_id)))}
                    inputProps={{ 'aria-label': 'Select all routes' }}
                  />
                </TableCell>
                <TableCell padding="checkbox" />
                <TableCell>Route</TableCell>
                <TableCell align="right">Cases</TableCell>
                <TableCell>Current default</TableCell>
                <TableCell>Accuracy trend</TableCell>
                <TableCell>{PICK_LABELS.recommended.label}</TableCell>
                <TableCell>{PICK_LABELS.quality.label}</TableCell>
                <TableCell>{PICK_LABELS.budget.label}</TableCell>
                <TableCell>Tested</TableCell>
                <TableCell>
                  <Tooltip title="Show this route's picks and tested accuracy on the demo site and in the platform portal. Test inputs and outputs are never published.">
                    <span>Published</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!routes.length ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No platform routes have test cases yet. Install the starter pack from the Test Library (platform account).
                  </TableCell>
                </TableRow>
              ) : (
                routes.map(r => {
                  const picks = r.tested?.picks;
                  const cur = r.tested?.models.find(m => m.model === r.current_default);
                  const open = expanded.has(r.family_id);
                  return (
                    <React.Fragment key={r.family_id}>
                    <TableRow hover selected={selected.has(r.family_id)} sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(r.family_id)} onChange={() => toggle(r.family_id)} inputProps={{ 'aria-label': `Select ${r.name}` }} />
                      </TableCell>
                      <TableCell padding="checkbox">
                        {!!r.tested?.models.length && (
                          <Tooltip title={open ? 'Hide models' : 'Compare every tested model'}>
                            <IconButton size="small" onClick={() => toggleExpanded(r.family_id)} aria-label={`Models for ${r.name}`}>
                              {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/dashboard/function-runs/${r.function_id}?tab=tests&customer_id=${encodeURIComponent(r.customer_id)}`}
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          {r.name}
                        </Link>
                        <Tooltip title={r.api_paths.join(', ') || 'No Nutrition API path mapped'}>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {r.platform_tool || r.kind || ''}
                            {r.api_paths.length ? ` · ${r.api_paths.length} API path${r.api_paths.length === 1 ? '' : 's'}` : ''}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{r.cases}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {modelLabel(r.current_default)}
                          {picks?.recommended && picks.recommended.model === r.current_default && (
                            <Chip size="small" color="success" variant="outlined" label="Recommended" sx={{ ml: 0.75, height: 18, fontSize: 11 }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <Score value={currentAccuracy(r)} /> acc
                          {cur ? ` · ${fmtPctValue(cur.pass_rate)} passing · ${fmtSeconds(cur.p95_s)} p95 · ${fmtCredits(cur.credits_per_run)} cr` : ''}
                        </Typography>
                        {r.applied && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            <Tooltip
                              title={`Was ${r.applied.previous_models.map(m => modelLabel(m)).join(', ') || 'unset'}; now ${r.applied.models
                                .map(m => modelLabel(m))
                                .join(', ')}`}
                            >
                              <span>
                                Picks applied {timeAgo(r.applied.at)}
                                {r.applied.by ? ` by ${r.applied.by}` : ''}
                              </span>
                            </Tooltip>
                            {' · '}
                            <Link component="button" variant="caption" onClick={() => undoApply(r)}>
                              Undo
                            </Link>
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <AccuracySpark points={r.trend} />
                      </TableCell>
                      <TableCell>
                        <PickCell pick={picks?.recommended} current={r.current_default} />
                      </TableCell>
                      <TableCell>
                        <PickCell pick={picks?.quality} current={r.current_default} />
                      </TableCell>
                      <TableCell>
                        <PickCell pick={picks?.budget} current={r.current_default} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.tested_run_id ? (
                          <Link component={RouterLink} to={runUrl(r.tested_run_id, q(r))} variant="body2">
                            {timeAgo(r.tested?.tested_at)}
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            not yet
                          </Typography>
                        )}
                        {r.tested && (
                          <Link component="button" variant="caption" color="text.secondary" onClick={() => toggleExpanded(r.family_id)} sx={{ display: 'block' }}>
                            {r.tested.models.length} models × {r.tested.repeats}
                          </Link>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={r.published_by ? `Published by ${r.published_by}` : r.tested ? '' : 'Test it first'}>
                          <span>
                            <Switch size="small" checked={r.published} disabled={!r.tested && !r.published} onChange={e => togglePublish(r, e.target.checked)} />
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {r.tested && (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                          <Collapse in={open} unmountOnExit>
                            <Box sx={{ py: 1.5 }}>
                              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                  {r.tested.cases} cases × {r.tested.repeats} runs each · tested {timeAgo(r.tested.tested_at)}
                                  {r.tested.note ? ` · ${r.tested.note}` : ''}
                                </Typography>
                                {r.tested_run_id && (
                                  <Link component={RouterLink} to={runUrl(r.tested_run_id, q(r))} variant="body2">
                                    Open full results
                                  </Link>
                                )}
                              </Stack>
                              <ModelComparisonTable
                                rows={rowsFromPublished(r.tested.models)}
                                defaultModel={r.current_default}
                                picks={r.tested.picks}
                                bar={r.tested.bar?.accuracy}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <SectionTitle
            title="Weekly test and digest"
            help="Re-tests every platform route with cases, so new models and regressions show up here. The digest goes to the admin alert recipients."
            action={
              platformApi && (
                <Button size="small" variant="outlined" onClick={() => setScheduleOpen(true)} disabled={schedule === undefined}>
                  {schedule ? 'Edit' : 'Set up'}
                </Button>
              )
            }
          />
          {schedule === undefined ? (
            <CircularProgress size={18} />
          ) : schedule ? (
            <Typography variant="body2">
              {scheduleText(schedule)} · {modelCountText(schedule.models)}
              {schedule.auto_new_models ? ' plus new ones' : ''} · {schedule.repeats} run{schedule.repeats === 1 ? '' : 's'} per case ·{' '}
              {schedule.email_digest ? 'emails a digest' : 'no email'}
              {schedule.next_run_at ? ` · next ${new Date(schedule.next_run_at).toLocaleString()}` : ''}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not scheduled.
            </Typography>
          )}
        </Paper>
        <SettingsCard />
      </Stack>
      {platformApi && (
        <ScheduleDialog
          open={scheduleOpen}
          api={platformApi}
          scope="platform"
          schedule={schedule || null}
          onClose={() => setScheduleOpen(false)}
          onSaved={s => {
            setScheduleOpen(false);
            setSchedule(s);
            refresh();
          }}
        />
      )}
      <ApplyPicksDialog
        open={!!applyTo}
        api={api}
        routes={(applyTo || []).map(r => ({ family_id: r.family_id, customer_id: r.customer_id, name: r.name || r.family_id }))}
        onClose={() => setApplyTo(null)}
        onApplied={n => {
          setApplyTo(null);
          setSelected(new Set());
          setNotice(`Applied picks to ${n} route${n === 1 ? '' : 's'}. New versions serve traffic within about a minute.`);
          refresh();
        }}
      />
      <PlatformTestDialog
        open={retestOpen}
        api={api}
        targets={chosen.map(r => ({
          function_id: r.function_id,
          customer_id: r.customer_id,
          name: r.name || r.family_id,
          kind: r.kind,
          default_model: r.current_default,
        }))}
        initialModels={retestModels}
        onClose={() => setRetestOpen(false)}
        onStarted={batches => {
          setRetestOpen(false);
          setSelected(new Set());
          setNotice(
            <>
              Started {batches.length} test batch{batches.length === 1 ? '' : 'es'}. Follow progress on{' '}
              <Link component={RouterLink} to="/dashboard/admin/platform-routes">
                Platform Routes
              </Link>
              ; results land here when they finish.
            </>,
          );
        }}
      />
    </Box>
  );
};

const SettingsCard: React.FC = () => {
  const { getIdToken } = useAuth();
  const api = useMemo(() => testsApi(getIdToken), [getIdToken]);
  const [settings, setSettings] = useState<TestSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const judgeOptions = useMemo(
    () => Object.values(MODEL_REGISTRY).filter(m => m.category === 'text' && m.isAvailable !== false && !isDeprecated(m.apiName) && !m.variantOf),
    [],
  );
  const imageOptions = useMemo(
    () => Object.values(MODEL_REGISTRY).filter(m => m.category === 'image' && m.isAvailable !== false && !isDeprecated(m.apiName) && !m.variantOf),
    [],
  );

  const modelSelect = (value: string, options: typeof judgeOptions, onChange: (v: string) => void) => (
    <Select size="small" value={value} onChange={e => onChange(e.target.value)} sx={{ minWidth: 220 }}>
      {!options.some(m => m.apiName === value) && <MenuItem value={value}>{modelLabel(value)}</MenuItem>}
      {options.map(m => (
        <MenuItem key={m.apiName} value={m.apiName}>
          {m.displayName}
        </MenuItem>
      ))}
    </Select>
  );

  useEffect(() => {
    api.settings().then(setSettings).catch(e => setError(errorText(e)));
  }, [api]);

  const save = async () => {
    if (!settings) return;
    setError(null);
    try {
      setSettings(await api.saveSettings(settings));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
      <SectionTitle
        title="Test settings"
        help="The judge model seeds each account's Test output judge function; after that, its own default model is used."
      />
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {!settings ? (
        <CircularProgress size={18} />
      ) : (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" sx={{ minWidth: 150 }}>
              Starting judge model
            </Typography>
            {modelSelect(settings.judge_model, judgeOptions, v => setSettings({ ...settings, judge_model: v }))}
          </Stack>
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
            <TextField
              label="Calls at once per run"
              type="number"
              size="small"
              value={settings.concurrency}
              onChange={e => setSettings({ ...settings, concurrency: Number(e.target.value) || 1 })}
              helperText="Up to 16"
              sx={{ width: 170 }}
            />
            <TextField
              label="Runs at once per account"
              type="number"
              size="small"
              value={settings.max_active_runs}
              onChange={e => setSettings({ ...settings, max_active_runs: Number(e.target.value) || 1 })}
              helperText="Up to 10"
              sx={{ width: 190 }}
            />
            <TextField
              label="Calls at once in total"
              type="number"
              size="small"
              value={settings.pool_size}
              onChange={e => setSettings({ ...settings, pool_size: Number(e.target.value) || 1 })}
              helperText="Shared by every run, per server (up to 64)"
              sx={{ width: 230 }}
            />
            <TextField
              label="Default credit cap"
              type="number"
              size="small"
              value={settings.default_credit_cap}
              onChange={e => setSettings({ ...settings, default_credit_cap: Number(e.target.value) || 0 })}
              helperText="Per run; runs get twice their estimate if that's higher"
              sx={{ width: 240 }}
            />
          </Stack>
          <Typography variant="subtitle2" sx={{ pt: 1 }}>
            Test case generator
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: '0 !important' }}>
            Admins use Generate on a function's or chain's Tests tab. Its two functions live in the generator account, which pays for them; the
            models below seed them until someone edits the functions.
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" sx={{ minWidth: 150 }}>
              Writes cases
            </Typography>
            {modelSelect(settings.generator_model, judgeOptions, v => setSettings({ ...settings, generator_model: v }))}
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" sx={{ minWidth: 150 }}>
              Makes images
            </Typography>
            {modelSelect(settings.media_model, imageOptions, v => setSettings({ ...settings, media_model: v }))}
          </Stack>
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
            <TextField
              label="Generator account"
              size="small"
              value={settings.generator_account}
              onChange={e => setSettings({ ...settings, generator_account: e.target.value })}
              helperText="Customer id or tag"
              sx={{ width: 200 }}
            />
            <TextField
              label="Cases per request"
              type="number"
              size="small"
              value={settings.generator_count}
              onChange={e => setSettings({ ...settings, generator_count: Number(e.target.value) || 5 })}
              helperText="Default, up to 10"
              sx={{ width: 170 }}
            />
          </Stack>
          {settings.generator?.error && (
            <Typography variant="caption" color="error">
              {settings.generator.error}
            </Typography>
          )}
          {!!settings.generator?.functions.length && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: '4px !important' }}>
              Functions:{' '}
              {settings.generator.functions.map((f, i) => (
                <React.Fragment key={f.function_id}>
                  {i > 0 && ', '}
                  <Link
                    component={RouterLink}
                    to={`/dashboard/function-runs/${f.function_id}?tab=performance&customer_id=${encodeURIComponent(settings.generator?.account_id || '')}`}
                  >
                    {f.name}
                  </Link>
                  {` (${modelLabel(f.models[0])}${f.edited ? ', edited' : ''})`}
                </React.Fragment>
              ))}
            </Typography>
          )}
          <Box>
            <Button size="small" variant="contained" onClick={save}>
              {saved ? 'Saved' : 'Save'}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};
